<template>
  <section class="contact-wrapper">
    <header class="contact-card screen-mount">
      <div class="contact-main">
        <p class="eyebrow">Sobre o desenvolvedor</p>
        <h1>João Pedro Vieira Brodt</h1>
        <p class="lead">
          Desenvolvedor full stack com atuação em portais de autoatendimento e plataformas de alto tráfego para Claro S.A.,
          Accurate Software e Sysmap Solutions. Combina front-end em Vue, React e Flutter com back-end em Java Spring
          Boot e Node.js, liderando entregas em ambientes regulados.
        </p>
        <div class="contact-links">
          <a
            v-for="link in contactChannels"
            :key="link.label"
            :href="link.href"
            target="_blank"
            rel="noopener"
          >
            <span>{{ link.label }}</span>
            <strong>{{ link.value }}</strong>
          </a>
        </div>
      </div>

      <ul class="contact-links">
        <li v-for="stat in stats" :key="stat.label">
          <span>{{ stat.label }}</span>
          <template v-if="stat.href">
            <a :href="stat.href" target="_blank" rel="noopener">{{ stat.value }}</a>
          </template>
          <template v-else>
            <strong>{{ stat.value }}</strong>
          </template>
        </li>
      </ul>
    </header>

    <section class="info-grid">
      <article class="info-card screen-mount" v-for="card in infoCards" :key="card.title">
        <h2>{{ card.title }}</h2>
        <ul>
          <li v-for="item in card.items" :key="item.label">
            <strong>{{ item.label }}</strong>
            <span>{{ item.value }}</span>
          </li>
        </ul>
      </article>
    </section>

    <article class="info-card screen-mount">
      <h2>Assuntos que atendo</h2>
      <div class="tag-grid">
        <span class="tag" v-for="topic in topics" :key="topic">{{ topic }}</span>
      </div>
    </article>
  </section>
</template>

<script>
export default {
  name: 'ContatoView',
  data() {
    return {
      contactChannels: [
        {
          label: 'LinkedIn',
          value: 'linkedin.com/in/joãobrodt',
          href: 'https://www.linkedin.com/in/jo%C3%A3obrodt/'
        },
        {
          label: 'GitHub',
          value: 'github.com/JoaoBrodt',
          href: 'https://github.com/JoaoBrodt'
        }
      ],
      stats: [
        { label: 'Experiência em portais', value: 'Claro S.A · Claro Móvel · Atendimento Técnico' },
        { label: 'Tecnologias dominadas', value: 'Vue, React, Avalonia, C#, Spring Boot, Node.js, Flutter' },
        { label: 'Tempo em desenvolvimento', value: '5+ anos focado em produtos digitais' },
      ],
      infoCards: [
        {
          title: 'Educação',
          items: [
            { label: 'PUC Campinas', value: 'Bacharelado em Ciência da Computação · 2020–2023' },
            { label: 'Universidade de São Paulo', value: 'Informática Biomédica · 2021–2024' },
            { label: 'Universidade Estadual de São Paulo', value: 'Mestrado em Computação · LLM para autotestes · 2023–Atual' }
          ]
        },
        {
          title: 'Experiência profissional',
          items: [
            { label: 'Accurate Software (2021–2024)', value: 'Front-end em Vue/React e APIs Spring Boot' },
            { label: 'Sysmap Solutions (2024–2025)', value: 'Referência técnica em suporte digital' },
            { label: 'Metodologias', value: 'Scrum, squads multidisciplinares, fluxos regulatórios' }
          ]
        },
        {
          title: 'Projetos e resultados',
          items: [
            { label: 'Claro S.A', value: 'Plataforma de autoatendimento e jornadas de swap' },
            { label: 'Claro Móvel', value: 'Portal cautivo de rentabilidade em Vue/TS' },
            { label: 'Atendimento técnico', value: 'Fluxos sensíveis (cancelamento/mudança) com Vue + Spring' }
          ]
        }
      ],
      topics: [
        'JavaScript · TypeScript',
        'C# · .NET',
        'Java · Spring Boot',
        'Vue.js · React.js',
        'Node.js · GraphQL',
        'Avalonia UI',
        'Flutter · Dart',
        'Docker · Git',
        'SQL · Firebase',
        'Scrum · DevOps'
      ],
      screenObserver: null
    }
  },
  mounted() {
    this.setupScreenObserver()
  },
  beforeUnmount() {
    if (this.screenObserver) {
      this.screenObserver.disconnect()
    }
  },
  methods: {
    setupScreenObserver() {
      if (this.screenObserver) {
        this.screenObserver.disconnect()
      }
      this.screenObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('screen-mount--visible')
            this.screenObserver.unobserve(entry.target)
          }
        })
      }, {
        threshold: 0.3,
        rootMargin: '0px 0px -10% 0px'
      })
      this.$nextTick(() => this.observeScreenMounts())
    },
    observeScreenMounts() {
      if (!this.screenObserver || !this.$el) return
      const sections = this.$el.querySelectorAll('.screen-mount')
      sections.forEach(section => {
        if (!section.classList.contains('screen-mount--visible')) {
          this.screenObserver.observe(section)
        }
      })
    }
  }
}
</script>

<style scoped>
@import '@/assets/animations/screenMount.css';
.contact-wrapper {
  max-width: 1100px;
  margin: 5rem auto 4rem;
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 3rem; /* Added top margin for spacing */
}

.contact-card {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 24px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.1);
  display: flex;
  gap: 2rem;
  padding: 2.2rem;
  flex-wrap: wrap;
  align-items: center;
}

.contact-main {
  flex: 1 1 320px;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 13px;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 0.4rem;
}

.contact-card h1 {
  font-size: 38px;
  margin-bottom: 0.6rem;
  color: #0f172a;
  white-space: nowrap;
}

.lead {
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1.4rem;
}

.contact-links {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.contact-links a, li {
  color: #0f172a;
  font-weight: 600;
  text-decoration: none;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 16px;
  transition: background 0.2s ease, transform 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  word-break: break-word;
}

.contact-links li {
 background: rgba(15, 23, 42, 0.02);
}

.contact-links a:hover {
  background: rgba(15, 23, 42, 0.05);
  transform: translateY(-2px);
}

.contact-links span {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #64748b;
}


.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.2rem;
}

.info-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 1.7rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.info-card h2 {
  font-size: 19px;
  margin-bottom: 0.9rem;
  color: #0f172a;
}

.info-card ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.info-card li {
  display: flex;
  flex-direction: column;
  color: #475569;
  gap: 0.2rem;
}

.info-card strong {
  color: #0f172a;
}

.tag-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag {
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
  font-weight: 600;
  font-size: 14px;
}

@media (max-width: 600px) {
  .contact-wrapper {
    padding: 0 1.5rem;
  }

  .contact-card {
    padding: 1.6rem;
    gap: 1.5rem;
    border-radius: 20px;
  }

  .contact-card h1 {
    white-space: normal;
  }

  .compact-stats {
    width: 100%;
    max-width: none;
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 0.75rem;
  }

  .compact-stats li {
    width: 100%;
    flex: none;
    border-radius: 16px;
  }

  .contact-links {
    width: 100%;
  }

  .contact-links a {
    width: 100%;
  }
}
</style>
